import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MoreHorizontal, Phone, Plus, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { walkInQueueApi, type StaffQueueEntry, type WalkInQueueReason } from '@/lib/api/walk-in-queue.js';
import { listCustomers, type CustomerListItem } from '@/lib/api/customers.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { getCurrentStore } from '@/lib/api/stores.js';
import { cn } from '@/lib/utils.js';

const REASONS: Array<{ id: WalkInQueueReason; label: string }> = [
  { id: 'walk_in', label: 'Walk-in' },
  { id: 'pickup', label: 'Pickup' },
  { id: 'appointment', label: 'Appointment' },
  { id: 'dropoff', label: 'Drop-off' },
  { id: 'question', label: 'Question' },
];

function reasonLabel(r: string) {
  return REASONS.find((x) => x.id === r)?.label ?? r;
}

function formatWait(mins: number) {
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

export function WalkInQueuePage() {
  const qc = useQueryClient();
  const storeQ = useQuery({ queryKey: ['store-current'], queryFn: getCurrentStore });
  const enabled = Boolean(storeQ.data?.queueEnabled);

  const boardQ = useQuery({
    queryKey: ['walk-in-queue'],
    queryFn: walkInQueueApi.list,
    enabled,
    refetchInterval: 8_000,
  });

  const [addOpen, setAddOpen] = useState(false);
  const [menuId, setMenuId] = useState<string | null>(null);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['walk-in-queue'] });
    void qc.invalidateQueries({ queryKey: ['walk-in-queue-status'] });
  };

  const callMut = useMutation({
    mutationFn: (id: string) => walkInQueueApi.call(id),
    onSuccess: () => {
      toastSuccess('Now serving');
      invalidate();
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const completeMut = useMutation({
    mutationFn: (id: string) => walkInQueueApi.complete(id),
    onSuccess: () => {
      toastSuccess('Completed');
      invalidate();
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const abandonMut = useMutation({
    mutationFn: (id: string) => walkInQueueApi.abandon(id),
    onSuccess: () => {
      toastSuccess('Marked abandoned');
      invalidate();
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const reorderMut = useMutation({
    mutationFn: ({ id, direction }: { id: string; direction: 'up' | 'down' }) =>
      walkInQueueApi.reorder(id, direction),
    onSuccess: invalidate,
    onError: (e) => toastError(parseApiError(e).message),
  });

  if (storeQ.isLoading) {
    return <div className="p-6 text-slate-600">Loading…</div>;
  }

  if (!enabled) {
    return (
      <div className="mx-auto max-w-lg rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
        <Users className="mx-auto h-10 w-10 text-slate-400" />
        <h1 className="mt-3 text-xl font-bold text-slate-950">Walk-in queue is off</h1>
        <p className="mt-2 text-sm text-slate-600">
          Enable it under Settings → Customer Display / Printing to show a lobby board and track
          who is waiting.
        </p>
      </div>
    );
  }

  const serving = boardQ.data?.serving ?? [];
  const waiting = boardQ.data?.waiting ?? [];

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-950">Walk-in Queue</h1>
          <p className="text-sm text-slate-500">People in the shop — not the repair ticket board</p>
        </div>
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-white shadow-sm"
        >
          <Plus className="h-4 w-4" /> Add to Queue
        </button>
      </div>

      <section className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-5 shadow-sm">
        <h2 className="text-xs font-bold uppercase tracking-wider text-emerald-800">Now serving</h2>
        {serving.length === 0 ? (
          <p className="mt-3 text-sm text-emerald-900/70">No one being helped right now</p>
        ) : (
          <ul className="mt-3 space-y-3">
            {serving.map((e) => (
              <li
                key={e.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-white px-4 py-3 shadow-sm"
              >
                <div>
                  <p className="text-lg font-bold text-slate-950">{e.displayName}</p>
                  <p className="text-sm text-slate-600">
                    {reasonLabel(e.reason)}
                    {e.ticketNumber ? ` · ${e.ticketNumber}` : ''}
                    {e.assignedToName ? ` · ${e.assignedToName}` : ''}
                    {e.calledAt
                      ? ` · ${formatWait(Math.floor((Date.now() - new Date(e.calledAt).getTime()) / 60000))} serving`
                      : ''}
                  </p>
                </div>
                <button
                  type="button"
                  className="rounded-xl bg-emerald-700 px-4 py-2 text-sm font-semibold text-white"
                  onClick={() => completeMut.mutate(e.id)}
                >
                  Complete
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500">
          Waiting ({waiting.length})
        </h2>
        {waiting.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500">Queue is empty</p>
        ) : (
          <ol className="mt-3 divide-y divide-slate-100">
            {waiting.map((e, i) => (
              <WaitingRow
                key={e.id}
                entry={e}
                index={i}
                menuOpen={menuId === e.id}
                onMenu={() => setMenuId(menuId === e.id ? null : e.id)}
                onCall={() => callMut.mutate(e.id)}
                onComplete={() => completeMut.mutate(e.id)}
                onAbandon={() => abandonMut.mutate(e.id)}
                onUp={() => reorderMut.mutate({ id: e.id, direction: 'up' })}
                onDown={() => reorderMut.mutate({ id: e.id, direction: 'down' })}
              />
            ))}
          </ol>
        )}
      </section>

      {addOpen ? (
        <AddToQueueModal
          onClose={() => setAddOpen(false)}
          onAdded={() => {
            setAddOpen(false);
            invalidate();
          }}
        />
      ) : null}
    </div>
  );
}

function WaitingRow({
  entry,
  index,
  menuOpen,
  onMenu,
  onCall,
  onComplete,
  onAbandon,
  onUp,
  onDown,
}: {
  entry: StaffQueueEntry;
  index: number;
  menuOpen: boolean;
  onMenu: () => void;
  onCall: () => void;
  onComplete: () => void;
  onAbandon: () => void;
  onUp: () => void;
  onDown: () => void;
}) {
  return (
    <li className="relative flex flex-wrap items-center justify-between gap-3 py-3">
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-slate-900">
          <span className="mr-2 text-slate-400">{index + 1}.</span>
          {entry.displayName}
          <span className="ml-2 text-sm font-normal text-slate-500">
            · {reasonLabel(entry.reason)}
            {entry.ticketNumber ? ` ${entry.ticketNumber}` : ''}
          </span>
        </p>
        <p className="mt-0.5 text-xs text-slate-500">waiting {formatWait(entry.waitingMinutes)}</p>
        {entry.notes ? <p className="mt-0.5 text-xs text-amber-800">{entry.notes}</p> : null}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-sm font-semibold text-white"
          onClick={onCall}
        >
          <Phone className="h-3.5 w-3.5" /> Call
        </button>
        <div className="relative">
          <button
            type="button"
            className="rounded-lg border border-slate-200 p-1.5 text-slate-600 hover:bg-slate-50"
            onClick={onMenu}
            aria-label="More"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
          {menuOpen ? (
            <div className="absolute right-0 z-20 mt-1 w-40 rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
              <button type="button" className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50" onClick={onUp}>
                Move up
              </button>
              <button type="button" className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50" onClick={onDown}>
                Move down
              </button>
              <button type="button" className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50" onClick={onComplete}>
                Complete
              </button>
              <button
                type="button"
                className="block w-full px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50"
                onClick={onAbandon}
              >
                Mark abandoned
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </li>
  );
}

function AddToQueueModal({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const [name, setName] = useState('');
  const [reason, setReason] = useState<WalkInQueueReason>('walk_in');
  const [notes, setNotes] = useState('');
  const [search, setSearch] = useState('');
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [hits, setHits] = useState<CustomerListItem[]>([]);

  useEffect(() => {
    if (search.trim().length < 2) {
      setHits([]);
      return;
    }
    const t = window.setTimeout(() => {
      void listCustomers({ q: search.trim(), pageSize: 8 })
        .then((r) => setHits(r.data ?? []))
        .catch(() => setHits([]));
    }, 250);
    return () => window.clearTimeout(t);
  }, [search]);

  const addMut = useMutation({
    mutationFn: () =>
      walkInQueueApi.add({
        displayName: name.trim() || 'Guest',
        customerId,
        reason,
        notes: notes.trim() || null,
      }),
    onSuccess: () => {
      toastSuccess('Added to queue');
      onAdded();
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <h3 className="text-lg font-bold text-slate-950">Add to queue</h3>
        <div className="mt-4 space-y-3">
          <label className="block text-sm">
            Search customer
            <input
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name or phone"
            />
          </label>
          {hits.length > 0 ? (
            <ul className="max-h-32 overflow-y-auto rounded-xl border border-slate-200">
              {hits.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50"
                    onClick={() => {
                      setCustomerId(c.id);
                      setName([c.firstName, c.lastName].filter(Boolean).join(' '));
                      setHits([]);
                      setSearch('');
                    }}
                  >
                    {c.firstName} {c.lastName}
                    {c.phone ? ` · ${c.phone}` : ''}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          <label className="block text-sm">
            Display name
            <input
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setCustomerId(null);
              }}
              required
            />
          </label>
          <label className="block text-sm">
            Reason
            <select
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={reason}
              onChange={(e) => setReason(e.target.value as WalkInQueueReason)}
            >
              {REASONS.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            Notes
            <input
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="rounded-xl border px-4 py-2 text-sm font-semibold" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            disabled={!name.trim() || addMut.isPending}
            className="rounded-xl bg-primary px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
            onClick={() => addMut.mutate()}
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );
}

/** Compact header badge: waiting count */
export function WalkInQueueHeaderChip() {
  const storeQ = useQuery({ queryKey: ['store-current'], queryFn: getCurrentStore, staleTime: 60_000 });
  const enabled = Boolean(storeQ.data?.queueEnabled);
  const statusQ = useQuery({
    queryKey: ['walk-in-queue-status'],
    queryFn: walkInQueueApi.status,
    enabled,
    refetchInterval: 15_000,
  });

  const count = statusQ.data?.waitingCount ?? 0;
  if (!enabled) return null;

  return (
    <Link
      to="/queue"
      className={cn(
        'hidden items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold sm:inline-flex',
        count > 0
          ? 'border-accent/40 bg-orange-50 text-accent'
          : 'border-slate-200 bg-white text-slate-600',
      )}
      title="Walk-in queue"
    >
      <Users className="h-3.5 w-3.5" />
      Queue{count > 0 ? ` ${count}` : ''}
    </Link>
  );
}
